import { ERROR_CODES, STORE_NAME } from '@intigral/constant';
import { AxiosLoggerRequestConfig } from '@intigral/constant/config/AxiosLoggerConfig';
import { isBrowser } from '@intigral/utils/lib/helpers/browser';
import { validUrl } from '@intigral/utils/lib/helpers/validators';
import axios, { Axios, AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { Header } from 'axios-cache-interceptor';
import * as AxiosLogger from 'axios-logger';
import { refreshAuthTokenApi } from '../../../utils/refreshAuthTokenApi';
import ResponseError from '../../core/entities/errors/ResponseError';
import {
    IHttpAdapter,
    IHttpErrorResponse,
    IHttpRequestConfig,
    IHttpResponse,
} from '../../entity/interface/iHttpAdapter';
import { container } from '../../DIContainer';
import { IProfileService } from '../../application/services/ProfileService';
import { DI } from '@intigral/constant/config/DIContainer';
import { get, set } from '@intigral/utils/lib/browser-storage/localStorage';
import { getConfigurationFromCache } from '../../services/ConfigurationService';

// Define the structure of a retry queue item

const APIS_ERRORS = ['MISSING_PROFILE_GUID', 'INVALID_PROFILE_GUID', 'NO_ACTIVE_PROFILE_GUID_IDENTITY_TOKEN'];
let isSwitchingProfile = false;
let switchProfileSubscribers: Array<(token: string) => void> = [];

const subscribeProfileSwitch = (cb: (token: string) => void) => {
    switchProfileSubscribers.push(cb);
};

const notifyProfileSwitchSubscribers = (token: string) => {
    switchProfileSubscribers.forEach((cb) => cb(token));
    switchProfileSubscribers = [];
};

const triggerForceLogout = () => {
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('force-logout'));
    }
};

const dispatchTrackingEvent = (errorData: ResponseError['data']) => {
    if (!isBrowser() || !errorData?.trackingEvent) return;
    window.dispatchEvent(new CustomEvent('TRACKING_EVENT_OCCURRED', { detail: errorData }));
};

const dispatchErrorEvent = (errorData: ResponseError['data']) => {
    if (!isBrowser()) return;
    window.dispatchEvent(new CustomEvent('API_ERROR_EVENT_OCCURRED', { detail: errorData }));
};

class AxiosHttp implements IHttpAdapter {
    constructor(private axios: Axios) {}

    get<T>(url: string, config?: IHttpRequestConfig): Promise<IHttpResponse<T>> {
        return this.handleRequest(() =>
            this.axios.request({
                method: 'get',
                url: url,
                headers: {
                    'Content-Type': 'application/json',
                },
                ...this.parseConfig(config),
            })
        );
    }

    post<T>(url: string, config?: IHttpRequestConfig): Promise<IHttpResponse<T>> {
        return this.handleRequest(() =>
            this.axios.request({
                method: 'post',
                url: url,
                headers: {
                    'Content-Type': 'application/json',
                },
                ...this.parseConfig(config),
            })
        );
    }

    put<T>(url: string, config?: IHttpRequestConfig): Promise<IHttpResponse<T>> {
        return this.handleRequest(() =>
            this.axios.request({
                method: 'put',
                url: url,
                ...this.parseConfig(config),
            })
        );
    }

    patch<T>(url: string, config?: IHttpRequestConfig): Promise<IHttpResponse<T>> {
        return this.handleRequest(() => this.axios.patch(url, undefined, this.parseConfig(config)));
    }

    delete<T>(url: string, config?: IHttpRequestConfig): Promise<IHttpResponse<T>> {
        return this.handleRequest(() =>
            this.axios.request({
                method: 'delete',
                url: url,
                ...this.parseConfig(config),
            })
        );
    }

    private parseConfig(config?: IHttpRequestConfig): AxiosRequestConfig | undefined {
        if (!config) return undefined;
        const { body, ...init } = config;
        return { ...init, data: body };
    }

    private async handleRequest<T>(request: () => Promise<AxiosResponse>): Promise<IHttpResponse<T>> {
        try {
            const result = await request();
            if (result.status >= 400) {
                throw new ResponseError({
                    url: result.request?.url,
                    response: { code: result.status, ...result } as IHttpResponse<IHttpErrorResponse>,
                });
            }
            if (result?.data?.error) {
                throw new ResponseError({
                    url: result.request?.url,
                    response: { code: result.status, ...result } as IHttpResponse<IHttpErrorResponse>,
                });
            }
            const data =
                typeof result.data === 'string' && result.data.includes('{') ? JSON.parse(result.data) : result.data;
            return {
                data,
                headers: Object.entries(result.headers).reduce((acc, [key, value]) => {
                    acc[key] = JSON.stringify(value);
                    return acc;
                }, {} as Record<string, string>),
                status: result.status,
                statusText: result.statusText,
            };
        } catch (error) {
            if (error instanceof AxiosError) {
                const req = error.request as { url: string };
                const response = error.response as IHttpResponse<IHttpErrorResponse>;
                const responseError = new ResponseError({ url: req?.url || '', response }, error);
                dispatchTrackingEvent(responseError.data);
                dispatchErrorEvent(responseError.data);
                throw responseError;
            }
            if (error instanceof ResponseError) {
                dispatchTrackingEvent(error.data);
                dispatchErrorEvent(error.data);
            }
            dispatchErrorEvent(error as any);
            throw error;
        }
    }
}

const updateIdentityTokenInStorage = (identityToken: string) => {
    const user = get(STORE_NAME.USER);
    if (!user) return;
    const updatedUser = {
        ...user,
        identityToken,
    };
    set(STORE_NAME.USER, updatedUser);
};

export const createAxiosInstance = (baseURL: string) => {
    return axios.create({ withCredentials: false, baseURL });
};

export const addLogInterceptorToHttpCalls = (httpInstance: any, avoidServerLogger = false) => {
    const avoidClientLogger = process.env['NX_PUBLIC_AVOID_CLIENT_LOGGER'] || true;
    const logLevel = (process.env['LOG_LEVEL'] as unknown as number) || 1;

    httpInstance.interceptors.request.use((config: any) => {
        if (!config.url) {
            return config;
        }
        let currentUrl: URL | { href: string };
        if (validUrl(config.url)) {
            /** @note Fully Valid URL in the path itself */
            currentUrl = new URL(config.url);
        } else if (validUrl(config.baseURL)) {
            currentUrl = new URL((config.baseURL || '') + config.url);
        } else {
            currentUrl = { href: config.url };
        }
        // parse pathName to implement variables
        // parse pathName to implement variables
        Object.entries(config.params || {}).forEach(([k, v]) => {
            const pattern = new RegExp(`(:${k}|{${k}}|%7B${k}%7D)`, 'g');
            const href = currentUrl.href.replace(pattern, () => encodeURIComponent(v as string));

            // If the replace is successful, remove the key from params obj
            if (href !== currentUrl.href) {
                delete config.params[k];
            }
            currentUrl.href = href;
        });

        /** @note Delete cache headers to avoid CORS issues */
        if (Header.CacheControl in config.headers) {
            delete config.headers[Header.CacheControl];
        }
        if (Header.Pragma in config.headers) {
            delete config.headers[Header.Pragma];
        }
        if (Header.Expires in config.headers) {
            delete config.headers[Header.Expires];
        }

        config = {
            ...config,
            url: currentUrl.href,
            baseURL: undefined,
            cache: config.requestCache || false,
        };

        if (logLevel > 3 || (avoidServerLogger && !isBrowser()) || (avoidClientLogger && isBrowser())) {
            return config;
        }

        return AxiosLogger.requestLogger(config, {
            ...AxiosLoggerRequestConfig,
            //prefixText: 'Bolt',
        });
    }, AxiosLogger.errorLogger);

    // Response Logger
    if (logLevel <= 3 && ((!avoidServerLogger && !isBrowser()) || (!avoidClientLogger && isBrowser()))) {
        httpInstance.interceptors.response.use(AxiosLogger.responseLogger, AxiosLogger.errorLogger);
    }

    return httpInstance;
};

// export const addResponseInterceptorToHttpCalls = (httpInstance: any) => {
//     window.localStorage.setItem(STORE_NAME.REFRESH_TOKEN_API_STATUS_IN_DD, 'false');
//     // TODO: Need to handle this when it triggers infinitely
//     httpInstance.interceptors.response.use(
//         async (response: any) => {
//             // if (response?.data?.error?.code && PROFILE_ERRORS.includes(response.data.error.code)) {
//             //     return handleSwitchProfileError(httpInstance, response);
//             // }
//             if (
//                 response?.data?.error &&
//                 response?.data?.error.code &&
//                 (response.data.error.code === ERROR_CODES.EXPIRED_TOKEN ||
//                     response.data.error.code === ERROR_CODES.EXPIRED_IDENTITY_TOKEN)
//             ) {
//                 await handleRefreshTokenError(httpInstance, response);
//             }
//             return response;
//         },
//         async (error: any) => {
//             return Promise.reject(error);
//         }
//     );
// };

export const addResponseInterceptorToHttpCalls = (httpInstance: any) => {
    if (typeof window !== 'undefined') {
        window.localStorage.setItem(STORE_NAME.REFRESH_TOKEN_API_STATUS_IN_DD, 'false');
    }
    httpInstance.interceptors.response.use(
        async (response: any) => {
            if (response?.data?.error?.code && APIS_ERRORS.includes(response.data.error.code)) {
                return handleSwitchProfileError(httpInstance, response);
            }
            if (
                response?.data?.error &&
                response?.data?.error.code &&
                (response.data.error.code === ERROR_CODES.EXPIRED_TOKEN ||
                    response.data.error.code === ERROR_CODES.EXPIRED_IDENTITY_TOKEN)
            ) {
                return handleRefreshTokenError(httpInstance, response);
            }
            return response;
        },

        async (error: any) => {
            const errorCode = error?.response?.data?.error?.code;
            if (errorCode && APIS_ERRORS.includes(errorCode)) {
                return handleSwitchProfileError(httpInstance, error.response);
            }
            if (
                errorCode &&
                (errorCode === ERROR_CODES.EXPIRED_TOKEN || errorCode === ERROR_CODES.EXPIRED_IDENTITY_TOKEN)
            ) {
                return handleRefreshTokenError(httpInstance, error.response);
            }

            return Promise.reject(error);
        }
    );
};

const handleRefreshTokenError = async (httpInstance: any, response: any) => {
    const originalRequest: AxiosRequestConfig & { _tokenRetry?: boolean } = {
        ...response.config,
        headers: { ...response.config?.headers },
    };

    // Prevent infinite retry loop
    if (originalRequest._tokenRetry) {
        return Promise.reject(response);
    }

    originalRequest._tokenRetry = true;

    const storedUser = get(STORE_NAME.USER) as { isGuest?: boolean; username?: string } | null | undefined;
    if (storedUser?.isGuest) {
        return Promise.reject(response);
    }

    const isRefreshing =
        typeof window !== 'undefined' ? window.localStorage.getItem(STORE_NAME.REFRESH_TOKEN_API_STATUS_IN_DD) : null;
    if (isRefreshing !== 'true') {
        try {
            // Refresh the access token
            if (typeof window !== 'undefined') {
                window.localStorage.setItem(STORE_NAME.REFRESH_TOKEN_API_STATUS_IN_DD, 'true');
            }
            const userData = await refreshAuthTokenApi();

            if (!userData?.authToken) {
                throw new Error('Invalid used data returned while refreshing token. Data:' + JSON.stringify(userData));
            }
            // Update the request headers with the new access token
            if (originalRequest?.headers?.['Authorization']) {
                originalRequest.headers['Authorization'] = userData.authToken;
            }
            if (originalRequest.headers?.['identityToken']) {
                originalRequest.headers['identityToken'] = userData.identityToken;
            }

            // Retry the original request
            return httpInstance(originalRequest);
        } finally {
            if (typeof window !== 'undefined') {
                window.localStorage.removeItem(STORE_NAME.REFRESH_TOKEN_API_STATUS_IN_DD);
            }
        }
    }

    return Promise.reject(response);
};

const handleSwitchProfileError = async (httpInstance: any, response: any) => {
    const originalRequest: AxiosRequestConfig & { _retry?: boolean } = {
        ...response.config,
        headers: {
            ...response.config?.headers,
        },
    };

    // Prevent infinite retry loop
    if (originalRequest._retry) {
        return Promise.reject(response);
    }

    originalRequest._retry = true;

    //  * If profile switching already running, queue this request
    if (isSwitchingProfile) {
        return new Promise((resolve, reject) => {
            subscribeProfileSwitch((newIdentityToken: string) => {
                try {
                    if (originalRequest.headers) {
                        originalRequest.headers['identityToken'] = newIdentityToken;
                    }
                    resolve(httpInstance(originalRequest));
                } catch (err) {
                    reject(err);
                }
            });
        });
    }

    isSwitchingProfile = true;

    try {
        const cmsConfigs = getConfigurationFromCache();
        const user = get(STORE_NAME.USER);
        const profile = get(STORE_NAME.SELECTED_PROFILE);
        const { profileGuid, profilePassword } = profile;
        const { apiKey, authToken, identityToken } = user;

        const result = await container
            .resolve<IProfileService>(DI.SERVICES.IProfileService)
            .switchProfile(apiKey, profileGuid, profile, profilePassword, authToken, identityToken);
        const newIdentityToken = result?.identityToken;

        if (!newIdentityToken) {
            throw new Error('No identityToken returned from switchProfileApi');
        }

        // Update local storage with new identity token
        updateIdentityTokenInStorage(newIdentityToken);

        // Update failed request headers
        if (originalRequest.headers) {
            originalRequest.headers['identityToken'] = newIdentityToken;
        }

        // Retry queued requests
        notifyProfileSwitchSubscribers(newIdentityToken);

        // Retry original request
        try {
            return await httpInstance(originalRequest);
        } catch (retryError: any) {
            const retryErrorCode = retryError?.data?.error?.code;
            // If retry still fails with profile errors, logout the user
            if (retryErrorCode && APIS_ERRORS.includes(retryErrorCode)) {
                if (cmsConfigs?.forceLogOut) {
                    triggerForceLogout();
                }
            }
            return Promise.reject(retryError);
        }
    } catch (error) {
        return Promise.reject(error);
    } finally {
        isSwitchingProfile = false;
    }
};

// const addInQueue = (config: AxiosRequestConfig) => {
//     const queueData = window.localStorage.getItem(STORE_NAME.REFRESH_TOKEN_RETRY_QUEUE);
//     const refreshAndRetryQueue = queueData ? JSON.parse(queueData) : [];
//     refreshAndRetryQueue.push({ config });
//     window.localStorage.setItem(STORE_NAME.REFRESH_TOKEN_RETRY_QUEUE, JSON.stringify(refreshAndRetryQueue));
// };

// const processQueue = (httpInstance: any) => {
//     const queueData = window.localStorage.getItem(STORE_NAME.REFRESH_TOKEN_RETRY_QUEUE);
//     const refreshAndRetryQueue = queueData ? JSON.parse(queueData) : [];
//     const userInfo = JSON.parse(window.localStorage.getItem(STORE_NAME.USER) || '');

//     refreshAndRetryQueue.forEach(({ config }: { config: AxiosRequestConfig }) => {
//         const updatedConfig = { ...config, headers: { ...config?.headers, "Authorization": userInfo?.authToken, "identityToken": userInfo?.identityToken } };
//         new Promise((resolve, reject) => {
//             httpInstance
//                 .request(updatedConfig)
//                 .then((response: any) => {
//                     resolve(response)
//                 })
//                 .catch((err: any) => {
//                     reject(err)
//                 });
//         });
//     });
//     // Clear the queue
//     window.localStorage.removeItem(STORE_NAME.REFRESH_TOKEN_RETRY_QUEUE);
// };

export default AxiosHttp;
